<template>
  <component
    :is="tag"
    class="list-row"
    :class="{
      live: tag === 'button' || tag === 'a',
      option: tag === 'li',
      wrap,
      whole,
      danger,
      inactive,
      selected,
      active,
      dotted: dot !== '',
      [`tone-${tone}`]: tone !== '',
    }"
    :type="tag === 'button' ? 'button' : undefined"
    :href="inactive ? undefined : link?.href.value"
    :role="link && inactive ? 'link' : undefined"
    :tabindex="link && inactive ? 0 : undefined"
    :aria-disabled="inactive && tag !== 'div' ? 'true' : undefined"
    :aria-checked="state === 'checked' ? String(selected) : undefined"
    :aria-selected="state === 'selected' ? String(selected || active) : undefined"
    @click="click"
  >
    <span
      v-if="icon && tint"
      class="circle"
      :class="{ muted }"
      :style="circleStyle"
      aria-hidden="true"
      ><component :is="icon" class="icon"
    /></span>
    <component :is="icon" v-else-if="icon" class="icon" aria-hidden="true" />
    <span v-else-if="dot" class="dot" :style="{ background: dot }" aria-hidden="true"></span>
    <span class="words">
      <span class="title"
        ><slot name="title">{{ title }}</slot></span
      >
      <span v-if="meta || $slots.meta" class="meta"
        ><slot name="meta">{{ meta }}</slot></span
      >
      <span v-if="$slots.below" class="below"><slot name="below" /></span>
    </span>
    <span v-if="$slots.tail" class="tail"><slot name="tail" /></span>
    <IconCheck v-if="selected" class="check" aria-hidden="true" />
    <IconChevronRight v-else-if="next" class="chevron" aria-hidden="true" />
  </component>
</template>

<script lang="ts">
import { computed, defineComponent, watchEffect } from 'vue'
import type { Component, PropType } from 'vue'
import { useLink } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import IconCheck from '~icons/mdi/check'
import IconChevronRight from '~icons/mdi/chevron-right'

const TAGS = ['button', 'router-link', 'div', 'li'] as const
export type RowTag = (typeof TAGS)[number]

/** Roles whose choice is read out as checked, and those read out as selected. */
const CHECKED = new Set(['radio', 'menuitemradio', 'checkbox', 'menuitemcheckbox', 'switch'])
const SELECTED = new Set([
  'option',
  'gridcell',
  'row',
  'tab',
  'treeitem',
  'columnheader',
  'rowheader',
])

/**
 * A row of a list (Ф-12, MOL-175): an icon of 24, a title 17/600, a meta of 13 in up to two lines, a
 * tail and a 20 chevron — 64 high, the hairline between rows drawn by the `AppCard list` around it.
 *
 *   as       — `button`, `router-link` (with `to`), `div` or `li`. Only a `div` may hold a button in its
 *              tail: a button inside a button is no HTML, and a screen reader hears one of them. An
 *              `li` is an option of a list a field owns (MOL-177): no stop of Tab, the focus stays in the
 *              field, and no hover — on a desktop it fought the arrows for which row is active (MOL-23);
 *   icon     — a component, drawn here at its step, so no screen can draw it at another;
 *   tint     — the icon stands in a circle of 40 (MOL-176): a colour — a category's, on its
 *              `cat-tint-share` — or `muted`, `surface-2` under `text-muted`, for what has no colour;
 *   dot      — a category's colour as a dot of 12 in place of an icon, and the row 52 (MOL-198, В-3
 *              «а»): the whole list of a spending's chips, a choice of one line with no meta. Chosen,
 *              the dot stands on a ring of `surface`: on the tint ten light categories fell under 3:1;
 *   wrap     — the title breaks onto lines instead of ending in «…»;
 *   whole    — the meta stands whole, in as many lines as it takes: an explanation cut at two lines
 *              explains nothing — the newcomer's steps on a phone of 320 (MOL-203). A list of rows
 *              keeps its two, so a long place does not grow the row without end;
 *   next     — the chevron (the owner's В-14 «а»): the row opens something to go on with — a screen, a
 *              sheet with fields, or a fork on the way to them (MOL-203). A row that acts at once or asks
 *              to confirm has none, and one card never mixes the two (DESIGN.md; held by review, the card
 *              is the screen's);
 *   tone     — the icon alone in `warn-ink` or `good-ink` (Ф-4, MOL-204): a status worth the eye — a
 *              receipt not accepted or not read, a receipt recorded; every other icon is `text-muted`, since
 *              the accent says «press here» and a status says nothing of the kind. `danger` and `inactive`
 *              colour the icon their own way, over it; not with `tint`, whose circle is the category's colour
 *              and stays so;
 *   danger   — the title and the icon in `bad-ink`: the row destroys;
 *   inactive — not now (Ф-6, MOL-174): focusable, `aria-disabled`, a press reaching nobody, the link
 *              followed nowhere — a link loses its `href` too, or a middle click or a long press would
 *              open it in a tab, and keeps its role and its place in the focus order by hand; the meta
 *              says why;
 *   selected — the fill, the ring and a ✓ (Ф-5), the weight unchanged; read out by the role the row
 *              was given — `aria-checked` for a radio, `aria-selected` for an option — never only seen;
 *   active   — the row the keyboard stands on in a list a field owns (`aria-activedescendant`, К-4):
 *              the fill without the ring, since the focus is in the field, and read out as selected, as
 *              the ARIA combobox has its active option.
 *
 * The slot `below` stands under the meta, in the column of the words and as wide as it — a tag,
 * «Отправляем…», at its own width, since it is inline; a bar of «Бюджет» across the column (MOL-192):
 * in the meta either would be cut with it at two lines.
 */
export default defineComponent({
  name: 'ListRow',
  components: { IconCheck, IconChevronRight },
  props: {
    as: {
      type: String as PropType<RowTag>,
      default: 'button',
      validator: (tag: string) => (TAGS as readonly string[]).includes(tag),
    },
    to: { type: [String, Object] as PropType<RouteLocationRaw>, default: undefined },
    title: { type: String, default: '' },
    meta: { type: String, default: '' },
    icon: { type: [Object, Function] as PropType<Component>, default: undefined },
    tint: { type: String, default: '' },
    dot: { type: String, default: '' },
    wrap: { type: Boolean, default: false },
    whole: { type: Boolean, default: false },
    next: { type: Boolean, default: false },
    tone: {
      type: String as PropType<'' | 'warn' | 'good'>,
      default: '',
      validator: (tone: string) => ['', 'warn', 'good'].includes(tone),
    },
    danger: { type: Boolean, default: false },
    inactive: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
  },
  emits: { click: (event: MouseEvent) => event instanceof MouseEvent },
  setup(props, { attrs, emit }) {
    // The tag is the row's for its life: a link is a link from the first render on.
    const link = props.as === 'router-link' ? useLink({ to: computed(() => props.to ?? '') }) : null
    const tag = props.as === 'router-link' ? 'a' : props.as
    const state = computed(() => {
      const role = typeof attrs.role === 'string' ? attrs.role : ''
      if (CHECKED.has(role)) return 'checked'
      if (SELECTED.has(role)) return 'selected'
      return null
    })
    if (import.meta.env.DEV) {
      if (link && props.to === undefined) console.warn('[ListRow] a link row needs `to`')
      // Drawn and not read out is the chosen-by-colour Ф-5 refuses: a picker gives its rows a role.
      watchEffect(() => {
        if (props.selected && state.value === null) {
          console.warn(
            '[ListRow] a selected row needs a role that carries a choice (radio, option…)',
          )
        }
      })
    }
    const muted = computed(() => props.tint === 'muted')
    const circleStyle = computed(() =>
      props.tint === '' || muted.value
        ? undefined
        : {
            color: props.tint,
            background: `color-mix(in oklch, ${props.tint} var(--cat-tint-share), var(--surface))`,
          },
    )
    function click(event: MouseEvent): void {
      if (props.inactive) {
        // Stopped as well as prevented, as the kit's button: nothing above hears a row that is not now.
        event.preventDefault()
        event.stopPropagation()
        return
      }
      emit('click', event)
      if (link && !event.defaultPrevented) void link.navigate(event)
    }
    return { link, tag, state, muted, circleStyle, click }
  },
})
</script>

<style scoped lang="scss">
.list-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  box-sizing: border-box;
  width: 100%;
  min-height: calc(var(--touch-target-lg) + var(--space-3));
  padding: var(--space-3) var(--space-4);
  color: var(--text);
  font-family: var(--font);
  font-size: var(--text-body);
  line-height: var(--leading-snug);
  text-align: left;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    @include focus-ring(-2px);
  }
}

/* The button's own look taken off, weaker than any class: the hairline the list card draws between its
   rows is a border too, and an equal selector here would take it off by the order sheets load in. */
:where(.list-row) {
  margin: 0;
  border: 0;
  background: none;
}

.live {
  cursor: pointer;
  transition: background-color var(--dur-fast) var(--ease-out);
}

.option {
  cursor: pointer;
}

@media (hover: hover) {
  .live:not(.inactive, .selected, .active):hover {
    background: var(--surface-2);
  }

  /* The muted circle is the hover's own fill: under the pointer it turns the other way, or it is gone
     (adversarial А3). */
  .live:not(.inactive, .selected, .active):hover .circle.muted {
    background: var(--surface);
  }
}

.icon {
  @include icon;

  font-size: var(--icon-md);
  color: var(--text-muted);
}

.circle {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--row-circle);
  height: var(--row-circle);
  border-radius: var(--radius-pill);

  .icon {
    color: inherit;
  }

  &.muted {
    background: var(--surface-2);
    color: var(--text-muted);
  }
}

.dotted {
  min-height: var(--touch-target-lg);
}

.dot {
  flex: none;
  width: var(--space-3);
  height: var(--space-3);
  border-radius: var(--radius-pill);

  /* On the tint a dot of ten light categories stood under 3:1; on `surface` each holds it (MOL-172 А2). */
  .selected & {
    box-shadow: 0 0 0 2px var(--surface);
  }
}

/* One column as wide as the words' place (MOL-318): an `auto` one took the least width of a line under them
   — a till's line of a purchase, on one line — and it ran under the amount, never cut by its «…». */
.words {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
}

.title {
  overflow: hidden;
  font-weight: var(--weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wrap .title {
  overflow-wrap: anywhere;
  white-space: normal;
}

/* A word longer than the column breaks rather than being cut at the side: the two lines end in «…», the
   middle of a word never in nothing (MOL-176, Е-11). */
.meta {
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  color: var(--text-muted);
  font-size: var(--text-footnote);
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.whole .meta {
  display: block;
  overflow: visible;
  -webkit-line-clamp: none;
  line-clamp: none;
}

.below {
  margin-top: var(--space-1);
}

/* The tail takes at most 45 % of the row, unless what it holds cannot be narrower — an amount never
   wraps — and gives way down to that: a line under an amount wraps between its parts, and the words keep
   the rest. Taken whole, a long line under the amount left the title no width at all and pushed the
   chevron past the card (MOL-176, adversarial А1, А2); at two fifths it broke a line the words had room
   to give (round 2, Б3). */
.tail {
  display: inline-flex;
  flex: 0 1 auto;
  align-items: center;
  min-width: min-content;
  max-width: 45%;
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
}

/* A button of 44 in the tail stands over the row's padding of 12 by 4 a side, so a row with a button is
   64 as every row is (DESIGN.md), its target still 44: grown by it, the rows of «Категории» were 68 and
   their skeleton 64 (MOL-191). Its sides are 8, not a button's 16, and it stands 8 into the row's padding
   at the right — its pill 8 from the card's edge, its word 16, the handoff's 8 and 20: beside a circle of
   40 a phone of 320 left the words 100 px, and «Развлечения» of 107 broke in the middle (adversarial А2),
   under «Вернуть», wider than «Убрать», with the sides alone still (Б1); now 113. */
.tail > :slotted(.button) {
  margin-block: calc(var(--space-1) * -1);
  margin-inline-end: calc(var(--space-2) * -1);
  padding-inline: var(--space-2);
}

/* In a narrow container named `row` the tail goes under the words, on a line of its own at the right —
   where the amounts end, so the column holds — and the words take the whole width: beside them an amount
   of a phone of 320 left the words forty pixels, and a salary with kopecks none (MOL-176, owner's choice
   on adversarial Б1, Б2). 22rem (352): from there beside the tail's 45 % the words keep some 80 px, a
   «Продукты» on its line; below it the word broke — the cards of «Деньги» on a phone of 390 and 412, 326
   and 348 in their gutter of 32. The handoff's card of 390 (358) keeps the amount beside. The container
   is the caller's — `OperationRow` sets it — so every row of a card is laid out alike. No row gap: a row
   with no tail is not a line taller. */
@mixin tail-under($whole: false) {
  .list-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0 var(--space-3);
  }

  .circle,
  .list-row > .icon {
    grid-row: 1 / span 2;
  }

  .words {
    grid-column: 2;
    grid-row: 1;
  }

  .tail {
    grid-column: 2;
    grid-row: 2;
    justify-self: end;
    max-width: none;
    margin-top: var(--space-1);
  }

  .chevron,
  .check {
    grid-column: 3;
    grid-row: 1 / span 2;
  }

  /* The whole row under the words, the circle and the chevron at the top: in the column of the words alone a
     sum at 200 % stood past the card's left edge — «100 000 ₽» read «00 000 ₽» (adversarial В5). The title
     breaks rather than end in «…», «Коп…» at 200 %. */
  @if $whole {
    .chevron,
    .check {
      grid-row: 1;
    }

    .tail {
      grid-column: 1 / -1;
    }

    /* The circle gives its place to the words, as the icon of `NavRow` and the wallet of `AccountRow` do: beside
       it and the chevron the words had some 40 px at 200 %, and «Обмен», «Продукты», «Карта» stood a letter or
       two a line. What the row is, its title says; the circle repeats it. */
    .circle,
    .list-row > .icon {
      display: none;
    }

    .words {
      grid-column: 1 / 3;
    }

    /* A category's dot stays before its name (adversarial К2): with no place of its own the grid put it in the first
       free cell — at the right, where the chevron and the check stand, or under the name. */
    .dot {
      grid-column: 1;
      grid-row: 1;
      align-self: center;
    }

    .dot ~ .words {
      grid-column: 2;
    }

    /* The title and the line under it whole, never «…»: at 200 % «Бот в Telegram» read «Б…», and the line of an
       exchange hid what came in. */
    .title {
      overflow: visible;
      overflow-wrap: anywhere;
      white-space: normal;
    }

    .meta {
      display: block;
      overflow: visible;
      -webkit-line-clamp: none;
      line-clamp: none;
    }
  }
}

@container row (width < #{$row-narrow}) {
  @include tail-under;
}

/* The same, only with large text, in a container named `row-tight` — every list card of the kit is one
   (`AppCard list`) — (MOL-321): the rows keep their tail beside the words at 100 % on 320 (16rem and more),
   and at 200 % «1 234 567,89 ₽» was cut by 68 px and «Убрать» by 37, the chevron past the phone. `$row-tight`. */
@container row-tight (width < #{$row-tight}) {
  @include tail-under($whole: true);
}

.chevron {
  @include icon;

  font-size: var(--icon);
  color: var(--text-muted);
}

.check {
  @include icon;

  font-size: var(--icon-md);
  color: var(--accent-ink);
}

.tone-warn > .icon {
  color: var(--warn-ink);
}

.tone-good > .icon {
  color: var(--good-ink);
}

.danger {
  .title,
  .icon {
    color: var(--bad-ink);
  }
}

.inactive {
  cursor: default;

  .title,
  .icon {
    color: var(--text-muted);
  }
}

.selected {
  position: relative;
  isolation: isolate;
}

/* The fill, the ring and the focus of a chosen row are a layer of their own, rounded as the list card,
   and the row stays square under them: rounded itself, the row bent the hairline the card draws as its
   `border-top` (round 3, Р3-1; adversarial Б1) — in an `li` a tile under a straight line, straight in
   the card a line bent into its round. Rounded, the ring of a first or last row follows the card's corner instead of being cut (А3). */
.selected::before {
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: var(--radius-lg);
  background: var(--accent-tint);
  box-shadow: inset 0 0 0 2px var(--accent);
  content: '';
}

/* The focus of a chosen row stands inside its ring, the fill between them: on the ring itself it was the
   same 2 px of the same colour, and the keyboard lost the row it stood on (adversarial А1, WCAG 2.4.7).
   Drawn on the layer, so it follows its round; taken off the row above the list card's own offset by
   weight, not by the order the sheets load in. */
.list-row.selected:focus-visible {
  outline: none;

  &::before {
    @include focus-ring(-6px);
  }
}

/* The meta stays muted on the three grounds only: on the tint under a chosen or an active row it stood at
   4.17:1 in the light scheme and 3.83:1 in the dark (MOL-172, MOL-177). */
.selected,
.active {
  .meta {
    color: var(--text);
  }
}

/* A chosen row takes its fill from its layer alone: the keyboard standing on the value already chosen drew
   a square fill out past the rounded ring at every corner (adversarial В1). */
.active:not(.selected) {
  background: var(--accent-tint);
}
</style>
